import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Target, ShoppingCart, Trophy, Bell, Wallet } from 'lucide-react';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';

import TasksTab from '../components/reseller/TasksTab';
import SalesTab from '../components/reseller/SalesTab';
import WalletTab from '../components/reseller/WalletTab';
import RankingTab from '../components/reseller/RankingTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('reseller_token')}`
});

const ResellerDashboard = () => {
  const [reseller, setReseller] = useState(null);
  const [config, setConfig] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sales, setSales] = useState([]);
  const [claims, setClaims] = useState([]);
  const [notices, setNotices] = useState([]);
  const [weeklyRanking, setWeeklyRanking] = useState([]);
  const [generalRanking, setGeneralRanking] = useState([]);
  const [rankingType, setRankingType] = useState('weekly');
  const [monthStats, setMonthStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState({});
  const [salesFilter, setSalesFilter] = useState({ start_date: '', end_date: '' });
  const [timeUntilReset, setTimeUntilReset] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [walletTx, setWalletTx] = useState([]);
  const [walletRedeemAmount, setWalletRedeemAmount] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [blueRefresh, setBlueRefresh] = useState(0);

  // Cronômetro até fim do mês (reset das tarefas MENSAIS)
  useEffect(() => {
    const calculateTimeUntilEndOfMonth = () => {
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return Math.floor((nextMonth - now) / 1000);
    };
    setTimeUntilReset(calculateTimeUntilEndOfMonth());
    const timer = setInterval(() => {
      const seconds = calculateTimeUntilEndOfMonth();
      setTimeUntilReset(seconds);
      if (seconds <= 0) {
        clearInterval(timer);
        loadResellerData();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadResellerData();
    loadRankings();
  }, []);

  const loadRankings = async () => {
    try {
      const [weeklyRes, generalRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reseller/ranking/weekly`),
        axios.get(`${BACKEND_URL}/api/reseller/ranking/admin`)
      ]);
      setWeeklyRanking(weeklyRes.data);
      setGeneralRanking(generalRes.data);
    } catch (error) {
      console.error('Erro ao carregar rankings:', error);
    }
  };

  const loadResellerData = async () => {
    try {
      const resellerRes = await axios.get(`${BACKEND_URL}/api/reseller/me`, { headers: authHeaders() });
      setReseller(resellerRes.data);
      localStorage.setItem('reseller_id', resellerRes.data.id);

      const [tasksRes, configRes, statsRes, claimsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reseller-tasks/my-progress/${resellerRes.data.id}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/config/`),
        axios.get(`${BACKEND_URL}/api/reseller/${resellerRes.data.id}/stats-month`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/reseller-tasks/${resellerRes.data.id}/my-claims`, { headers: authHeaders() })
      ]);
      setTasks(tasksRes.data || []);
      setConfig(configRes.data);
      setMonthStats(statsRes.data);
      setClaims(claimsRes.data || []);

      // Avisos
      try {
        const noticesRes = await axios.get(`${BACKEND_URL}/api/reseller-notices/active`);
        setNotices(noticesRes.data);
      } catch (err) {
        setNotices([]);
      }

      // Carteira
      try {
        const [walletRes, walletTxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/reseller/wallet/me`, { headers: authHeaders() }),
          axios.get(`${BACKEND_URL}/api/reseller/wallet/transactions`, { headers: authHeaders() })
        ]);
        setWallet(walletRes.data);
        setWalletTx(walletTxRes.data || []);
      } catch (err) {
        console.log('Carteira não carregada:', err);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        window.location.href = '/reseller/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (taskId) => {
    if (!reseller || claiming[taskId]) return;
    setClaiming({ ...claiming, [taskId]: true });
    try {
      await axios.post(`${BACKEND_URL}/api/reseller-tasks/claim-reward`, null, {
        params: { reseller_id: reseller.id, task_id: taskId },
        headers: authHeaders()
      });
      toast.success('🎉 Recompensa creditada com sucesso!');
      loadResellerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resgatar');
    } finally {
      setClaiming({ ...claiming, [taskId]: false });
    }
  };

  // Chamado pelo WalletTab APÓS confirmação do AlertDialog
  const handleWalletRedeem = async (coins) => {
    if (!wallet || redeeming) return;
    if (!coins || coins < wallet.min_redemption_coins) {
      toast.error(`O resgate mínimo é de ${wallet.min_redemption_coins.toLocaleString()} moedas.`);
      return;
    }
    if (coins > wallet.wallet_balance) {
      toast.error('Saldo insuficiente.');
      return;
    }
    if (!wallet.redemptions_enabled) {
      toast.error('Resgates estão temporariamente desabilitados.');
      return;
    }
    setRedeeming(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/reseller/wallet/redeem`,
        { coins },
        { headers: authHeaders() }
      );
      toast.success(res.data?.message || '🎉 Resgate realizado!');
      setWalletRedeemAmount('');
      loadResellerData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resgatar da carteira');
    } finally {
      setRedeeming(false);
    }
  };

  // Chamado pelo WalletTab APÓS confirmação do AlertDialog (intercâmbio)
  const handleWalletExchange = async (coins) => {
    if (!wallet || exchanging) return;
    setExchanging(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/reseller/wallet/exchange`,
        { coins },
        { headers: authHeaders() }
      );
      toast.success(res.data?.message || '💙 Intercâmbio realizado!');
      setBlueRefresh((k) => k + 1);
      loadResellerData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer o intercâmbio');
    } finally {
      setExchanging(false);
    }
  };

  const loadSales = async () => {
    if (!reseller) return;
    try {
      const params = {};
      if (salesFilter.start_date) params.start_date = salesFilter.start_date;
      if (salesFilter.end_date) params.end_date = salesFilter.end_date;
      const response = await axios.get(`${BACKEND_URL}/api/reseller/${reseller.id}/sales`, {
        headers: authHeaders(),
        params
      });
      setSales(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
    }
  };

  useEffect(() => {
    if (reseller) loadSales();
  }, [reseller, salesFilter]);

  const handleLogout = () => {
    localStorage.removeItem('reseller_token');
    localStorage.removeItem('reseller_refresh');
    window.location.href = '/';
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(resellerLink);
    toast.success('Link copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  const resellerLink = `${window.location.origin}/?ref=${reseller?.kako_id || ''}`;

  // Contagem de tabs para o grid
  let tabsCount = 3; // Vendas + Carteira + Avisos
  if (config?.reseller_tasks_active) tabsCount++;
  if (config?.reseller_ranking_active) tabsCount++;
  const tabsGridClass = {
    3: 'grid w-full grid-cols-3',
    4: 'grid w-full grid-cols-4',
    5: 'grid w-full grid-cols-5'
  }[tabsCount] || 'grid w-full grid-cols-3';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              {reseller?.kako_avatar && (
                <img
                  src={reseller.kako_avatar}
                  alt={reseller.nickname}
                  className="w-16 h-16 rounded-full border-4 border-white shadow-lg"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold">Painel do Afiliado</h1>
                <p className="text-sm opacity-90">Bem-vindo, {reseller?.nickname || reseller?.kako_id}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 text-white border-white/30"
              data-testid="reseller-logout-btn"
            >
              Sair
            </Button>
          </div>
        </div>

        {/* KPIs do mês */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
          <Card>
            <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
              <CardTitle className="text-[11px] sm:text-sm">Vendas do Mês</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <p className="text-xl sm:text-2xl font-bold text-purple-600">{monthStats?.total_orders || 0}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">pedidos</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
              <CardTitle className="text-[11px] sm:text-sm">Moedas do Mês</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {monthStats?.total_coins?.toLocaleString() || 0} <span className="text-base">🪙</span>
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">vendidas</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
              <CardTitle className="text-[11px] sm:text-sm">Bônus Resgatados</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {monthStats?.total_bonuses_claimed?.toLocaleString() || 0} <span className="text-base">🎁</span>
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">desde sempre</p>
            </CardContent>
          </Card>
        </div>

        {/* Link de indicação */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Seu Link de Indicação</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input value={resellerLink} readOnly className="font-mono text-sm" data-testid="reseller-link-input" />
              <Button onClick={handleCopyLink} data-testid="reseller-link-copy-btn">
                Copiar
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Compartilhe este link para ganhar comissões nas vendas
            </p>
          </CardContent>
        </Card>

        {/* Tabs principais */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Minhas Comissões e Tarefas</CardTitle>
              {config?.reseller_tasks_active && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Reset Tarefas:</p>
                  <div className="text-sm font-bold text-orange-600">
                    {Math.floor(timeUntilReset / 86400)}d {Math.floor((timeUntilReset % 86400) / 3600)}h{' '}
                    {Math.floor((timeUntilReset % 3600) / 60)}m
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="sales" className="w-full">
              <TabsList className={tabsGridClass}>
                {config?.reseller_tasks_active && (
                  <TabsTrigger value="tasks" data-testid="tab-tasks">
                    <Target className="w-4 h-4 mr-2" />
                    Tarefas
                  </TabsTrigger>
                )}
                <TabsTrigger value="sales" data-testid="tab-sales">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Vendas
                </TabsTrigger>
                <TabsTrigger value="wallet" data-testid="tab-wallet">
                  <Wallet className="w-4 h-4 mr-2" />
                  Carteira
                </TabsTrigger>
                <TabsTrigger value="avisos" data-testid="tab-avisos">
                  <Bell className="w-4 h-4 mr-2" />
                  Avisos
                </TabsTrigger>
                {config?.reseller_ranking_active && (
                  <TabsTrigger value="ranking" data-testid="tab-ranking">
                    <Trophy className="w-4 h-4 mr-2" />
                    Ranking
                  </TabsTrigger>
                )}
              </TabsList>

              {config?.reseller_tasks_active && (
                <TabsContent value="tasks" className="mt-4">
                  <TasksTab tasks={tasks} claims={claims} claiming={claiming} onClaim={handleClaimReward} />
                </TabsContent>
              )}

              <TabsContent value="sales" className="mt-4">
                <SalesTab
                  reseller={reseller}
                  sales={sales}
                  salesFilter={salesFilter}
                  setSalesFilter={setSalesFilter}
                  onFilter={loadSales}
                />
              </TabsContent>

              <TabsContent value="wallet" className="mt-4">
                <WalletTab
                  wallet={wallet}
                  walletTx={walletTx}
                  walletRedeemAmount={walletRedeemAmount}
                  setWalletRedeemAmount={setWalletRedeemAmount}
                  onRedeem={handleWalletRedeem}
                  redeeming={redeeming}
                  onExchange={handleWalletExchange}
                  exchanging={exchanging}
                  blueRefresh={blueRefresh}
                />
              </TabsContent>

              <TabsContent value="avisos" className="mt-4">
                <div className="space-y-3">
                  {notices.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Nenhum aviso no momento</p>
                  ) : (
                    notices.map((notice) => (
                      <Card key={notice.id} className="border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-blue-600 mt-1" />
                            <div className="flex-1">
                              <h4 className="font-bold text-blue-900 mb-2">{notice.title}</h4>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{notice.message}</p>
                              {notice.button_text && notice.button_url && (
                                <a
                                  href={notice.button_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block"
                                >
                                  <Button className="bg-green-600 hover:bg-green-700">{notice.button_text}</Button>
                                </a>
                              )}
                              <p className="text-xs text-gray-500 mt-3">
                                📅 {new Date(notice.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {config?.reseller_ranking_active && (
                <TabsContent value="ranking" className="mt-4">
                  <RankingTab
                    reseller={reseller}
                    weeklyRanking={weeklyRanking}
                    generalRanking={generalRanking}
                    rankingType={rankingType}
                    setRankingType={setRankingType}
                  />
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResellerDashboard;
